import { Card, CardContent, CardTitle } from '@/components/ui/Card.tsx';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  BookingSummaryForm,
  BookingSummaryInputForm,
  createBookingSummarySchema,
} from '@/features/public/booking/schemas/bookingSummarySchema.ts';
import { debugZodResolver } from '@/lib/debugZodResolver';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import { ArrowLeft } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { bookingRoute } from '@/features/public/booking/routes/bookingRoutes.ts';
import {
  bookingDraftKey,
  draftOf,
  useBookingStore,
} from '@/features/public/booking/context/bookingStore.tsx';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { buttonVariants } from '@/components/buttons/actionButtonVariants.ts';
import { cn } from '@/lib/utils.ts';
import { useCreateBooking } from '@/features/public/booking/hooks/useCreateBooking.ts';
import { getBookingQuoteQueryOptions } from '@/features/public/booking/hooks/useGetBookingQuote.ts';
import { useRegistrationAvailabilityGuard } from '@/features/public/booking/hooks/useRegistrationAvailabilityGuard.ts';
import InstallmentSummaryCard from '@/features/public/booking/components/InstallmentSummaryCard.tsx';
import useGetTourConsents from '@/features/public/booking/hooks/useGetTourConsents.ts';
import TourConsentCheckbox from '@/features/public/booking/components/TourConsentCheckbox.tsx';
import PromoCodeBookingSection from '@/features/public/booking/components/PromoCodeBookingSection.tsx';
import BookingSummarySkeleton from '@/features/public/booking/components/BookingSummarySkeleton.tsx';
import { toBookingRequest } from '@/features/public/booking/types/BookingSpecification.mapper.ts';
import { isBookingPriceChangedError } from '@/features/public/booking/api/bookingErrors.ts';
import { getEstimatedPricesString } from '@/features/shared/pricing/util/PricingFormatter.ts';
import { getTemporalIdForIndex } from '@/features/shared/booking/utils/temporalId.ts';
import { toPricingIssues } from '@/features/public/booking/util/pricingIssues.ts';
import PricingIssuesNotice from '@/features/public/booking/components/PricingIssuesNotice.tsx';
import MultiStepLoader from '@/components/ui/MultiStepLoader.tsx';
import { BOOKING_PROGRESS_STEPS } from '@/features/public/booking/components/bookingProgressSteps.ts';
import { BookingResponseDto } from '@/features/public/booking/types/BookingResponseDto.ts';
import useVirtualKeyboardOpen from '@/hooks/useVirtualKeyboardOpen.ts';

const BookingSummaryPage = () => {
  const { participantForm } = bookingRoute.useLoaderData();
  const { orgSlug, tourSlug } = useParams({
    from: '/booking/$orgSlug/$tourSlug/summary',
  });

  const tourKey = bookingDraftKey(orgSlug, tourSlug);
  const draftFormData = useBookingStore(
    (state) => draftOf(state, tourKey).clientFormData,
  );
  const draftPromoCodes = useBookingStore(
    (state) => draftOf(state, tourKey).promoCodes,
  );
  const clearDraft = useBookingStore((state) => state.clearDraft);
  const navigate = useNavigate();

  const fieldDefinitions = participantForm ?? [];

  const [isBooking, setIsBooking] = useState(false);
  const [booking, setBooking] = useState<BookingResponseDto | null>(null);
  const bookingStarted = isBooking || booking !== null;

  const createBookingMutation = useCreateBooking();
  // Jak w stopce kroku 1: nad klawiaturą pasek zasłaniałby pole kodu promocyjnego.
  const keyboardOpen = useVirtualKeyboardOpen();

  /**
   * Szkic kasujemy w chwili sukcesu, a ten ekran żyje jeszcze kilkaset
   * milisekund dłużej — nakładka musi domknąć oś. To, co poszło na serwer,
   * trzyma react-query w `variables`; bez tego `generateFormKey` dostałby
   * `undefined` i strona wysypałaby się w trakcie składania rezerwacji.
   */
  const submitted = createBookingMutation.variables;
  const clientFormData = draftFormData ?? submitted?.clientForm;
  const promoCodes = draftFormData
    ? draftPromoCodes
    : (submitted?.promoCodes ?? []);

  const summaryQuery = useQuery({
    ...getBookingQuoteQueryOptions(
      orgSlug,
      tourSlug,
      clientFormData!,
      fieldDefinitions,
      promoCodes,
    ),
    enabled: !!clientFormData && !bookingStarted,
  });

  // Ta sama ochrona co na kroku 1: odmowa z powodu okna zapisów przełącza route
  // na ekran z właściwym powodem, zamiast zostawiać klienta na podsumowaniu.
  useRegistrationAvailabilityGuard(summaryQuery.error, orgSlug, tourSlug);

  const { data: tourConsents } = useGetTourConsents(orgSlug, tourSlug);

  const requiredConsentIds = useMemo(
    () => tourConsents?.filter((c) => c.required).map((c) => c.consentId) ?? [],
    [tourConsents],
  );

  const schema = useMemo(
    () => createBookingSummarySchema(requiredConsentIds),
    [requiredConsentIds],
  );

  const summaryForm = useForm<BookingSummaryInputForm>({
    resolver: debugZodResolver(schema),
    defaultValues: {
      paymentMethod: PaymentMethod.TPAY,
      tourConsents: {},
    },
  });

  useEffect(() => {
    if (tourConsents) {
      const defaults: Record<string, boolean> = {};
      tourConsents.forEach((consent) => {
        defaults[consent.consentId] = false;
      });
      summaryForm.setValue('tourConsents', defaults);
    }
  }, [tourConsents]);

  const [priceChanged, setPriceChanged] = useState(false);

  // Wiersze wyceny w kolejności formularza — korelacja po temporalId (UUID).
  const orderedParticipants = useMemo(
    () =>
      (clientFormData?.participants ?? []).map((_, index) =>
        summaryQuery.data?.pricesPerParticipants?.find(
          (p) => p.temporalId === getTemporalIdForIndex(index),
        ),
      ),
    [summaryQuery.data, clientFormData],
  );

  // Błędy wyceny per uczestnik, z etykietami do wyświetlenia. Jakikolwiek błąd
  // (u dowolnego uczestnika) oznacza brak paymentPlan i odrzucenie book.
  const pricingIssues = useMemo(
    () =>
      toPricingIssues(clientFormData?.participants ?? [], orderedParticipants),
    [clientFormData, orderedParticipants],
  );

  const onSubmit = async (formData: BookingSummaryForm) => {
    // Rezerwacja jest jednorazowa. Po sukcesie ekran zostaje na planszy
    // potwierdzenia (albo na jej zapasowej wersji, gdy przejście padło), a
    // każdy kolejny submit musiałby założyć drugą rezerwację tego samego.
    if (bookingStarted || createBookingMutation.isSuccess) {
      return;
    }
    if (!clientFormData || !summaryQuery.data) {
      return;
    }
    // Bez ostatecznej wyceny book skończy się 400 — serwer i tak to odrzuci.
    if (!summaryQuery.data.paymentPlan) {
      return;
    }
    setPriceChanged(false);
    setIsBooking(true);
    setBooking(null);

    const acceptedConsentIds = Object.entries(formData.tourConsents)
      .filter(([, accepted]) => accepted)
      .map(([consentId]) => consentId);

    createBookingMutation.mutate(
      {
        orgSlug,
        tourSlug,
        clientForm: clientFormData,
        fieldDefinitions,
        acceptedConsentIds,
        promoCodes,
        expectedTotals: summaryQuery.data.total.map((price) => price.amount),
      },
      {
        onSuccess: (data) => {
          // Rezerwacja jest złożona, ale ekran zmieniamy dopiero, gdy nakładka
          // dojedzie do końca — `handleBookingFinished` domyka przejście.
          setBooking(data);
          // Szkic ginie tu, a nie po przejściu: zamknięta karta i „wstecz” nie
          // mogą wznowić formularza rezerwacji, która już istnieje.
          clearDraft(tourKey);
        },
        onError: (error) => {
          setIsBooking(false);
          setBooking(null);
          if (isBookingPriceChangedError(error)) {
            setPriceChanged(true);
            summaryQuery.refetch();
            return;
          }
          console.error('Booking failed:', error);
        },
      },
    );
  };

  /** Ostatni krok nakładki odstał swoje — dopiero teraz zmieniamy ekran. */
  const handleBookingFinished = async () => {
    if (!booking) return;

    try {
      await navigate({
        to: '/booking/$orgSlug/summary',
        params: { orgSlug },
        search: { token: booking.bookingSummaryToken },
      });
    } catch (error) {
      // Bez tego nieudane przejście zostawiłoby klienta pod nakładką na zawsze.
      // Rezerwacja jest już złożona, więc zamiast formularza dostaje
      // potwierdzenie i odnośnik, którym dojdzie do niej sam.
      setIsBooking(false);
      console.error('Booking navigation failed:', error);
    }
  };

  /**
   * Jedyne wyjście na czas składania rezerwacji i pierwsze w kolejności.
   *
   * Nakładka niesie własny zegar: gdyby zgasła choć na jedno przerysowanie,
   * `onFinished` nigdy by nie padło i przejście na potwierdzenie przepadłoby
   * razem z nim — przy złożonej już rezerwacji. Dlatego stoi przed każdym
   * innym `return`: ani przeładowanie wyceny (refetch po powrocie do karty),
   * ani zniknięcie szkicu po sukcesie nie mogą jej ruszyć.
   */
  if (isBooking) {
    return (
      <MultiStepLoader
        steps={BOOKING_PROGRESS_STEPS}
        loading
        done={booking !== null}
        onFinished={handleBookingFinished}
      />
    );
  }

  // Rezerwacja stoi, a przejścia nie było — zostaje odnośnik do potwierdzenia.
  if (booking) {
    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-3">
          <CardTitle>Rezerwacja przyjęta</CardTitle>
          <p className="text-sm text-neutral-600">
            Nie udało się otworzyć potwierdzenia automatycznie. Przejdź do
            niego, aby zobaczyć szczegóły i opłacić rezerwację.
          </p>
          <Link
            to="/booking/$orgSlug/summary"
            params={{ orgSlug }}
            search={{ token: booking.bookingSummaryToken }}
            className={cn(buttonVariants({ tone: 'accent', size: 'sm' }))}
          >
            Przejdź do potwierdzenia
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (!clientFormData) {
    return null;
  }

  if (summaryQuery.isLoading) {
    return <BookingSummarySkeleton />;
  }

  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <Card>
        <CardContent>
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
            Nie udało się obliczyć podsumowania rezerwacji. Spróbuj odświeżyć
            stronę lub wróć do poprzedniego kroku.
          </div>
        </CardContent>
      </Card>
    );
  }

  const { paymentPlan, total, appliedDiscounts, promoCodeStatuses } =
    summaryQuery.data;

  //TODO: Refactor price formatting function (extract to utils)
  return (
    <FormProvider {...summaryForm}>
      <form onSubmit={summaryForm.handleSubmit(onSubmit)} className="space-y-2">
        <Card>
          <PromoCodeBookingSection
            tourKey={tourKey}
            appliedDiscounts={appliedDiscounts}
            promoCodeStatuses={promoCodeStatuses}
            isRecalculating={summaryQuery.isFetching}
          />

          <CardContent>
            <CardTitle className="mb-4">Podsumowanie kosztów</CardTitle>
            <div className="space-y-2">
              <div className="flex flex-row justify-between">
                <div className="text-lg font-medium">Koszt całkowity</div>
                <div className="font-semibold">
                  {getEstimatedPricesString(total)}
                </div>
              </div>
              {clientFormData?.participants?.map((participant, index) => (
                <div key={index} className="flex flex-row justify-between">
                  <div>
                    {participant.firstName} {participant.lastName}
                  </div>
                  <div>
                    {getEstimatedPricesString(
                      orderedParticipants[index]?.estimatedPrice,
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>

          {!paymentPlan && (
            <CardContent>
              {/* Brak harmonogramu ma dwie przyczyny i klient musi wiedzieć,
                  która go dotyczy: albo coś jest nie tak z wyborem, albo
                  formularz jest jeszcze niekompletny. */}
              {pricingIssues.length > 0 ? (
                <PricingIssuesNotice
                  issues={pricingIssues}
                  hint="Wróć do poprzedniego kroku i zmień wybór, aby dokończyć rezerwację."
                />
              ) : (
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
                  Wycena nie jest jeszcze ostateczna. Wróć do poprzedniego kroku
                  i uzupełnij brakujące dane, aby dokończyć rezerwację.
                </div>
              )}
            </CardContent>
          )}

          {paymentPlan && (
            <CardContent className="space-y-2">
              <p className="text-lg font-medium">Podsumowanie opłat</p>
              {paymentPlan.installments.map((installment, index) => (
                <InstallmentSummaryCard
                  key={index}
                  installment={installment}
                  index={index}
                />
              ))}
            </CardContent>
          )}

          <CardContent className="space-y-2">
            <p className="text-lg font-medium">Zgody</p>
            <CheckboxInput
              label={
                <>
                  {'Akceptuję regulamin serwisu Campdesk '}
                  <a
                    href="https://campdesk.pl/regulamin/"
                    target="_blank"
                    className="text-primary-600 underline"
                  >
                    (podgląd)
                  </a>
                </>
              }
              register={summaryForm.register('termsConsent')}
              error={summaryForm.formState.errors.termsConsent?.message}
              required
            />
            {tourConsents?.map((consent) => (
              <TourConsentCheckbox
                key={consent.consentId}
                consent={consent}
                register={summaryForm.register(
                  `tourConsents.${consent.consentId}`,
                )}
                error={
                  summaryForm.formState.errors.tourConsents?.[consent.consentId]
                    ?.message
                }
                orgSlug={orgSlug}
                tourSlug={tourSlug}
                bookingSpecification={toBookingRequest(
                  clientFormData,
                  fieldDefinitions,
                )}
              />
            ))}
          </CardContent>

          {priceChanged && (
            <CardContent>
              <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-700">
                Cena rezerwacji uległa zmianie od momentu wyświetlenia
                podsumowania. Powyżej znajduje się zaktualizowana cena — sprawdź
                ją i potwierdź rezerwację ponownie.
              </div>
            </CardContent>
          )}

          {createBookingMutation.error && !priceChanged && (
            <CardContent>
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                {createBookingMutation.error.message ||
                  'Wystąpił błąd podczas tworzenia rezerwacji. Spróbuj ponownie.'}
              </div>
            </CardContent>
          )}
        </Card>

        <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-between">
          <ActionButton
            tone="outline"
            size="sm"
            onClick={() => {
              window.history.back();
            }}
            disabled={isBooking}
          >
            <ArrowLeft />
            Wstecz
          </ActionButton>

          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={isBooking || summaryQuery.isFetching}
            disabled={!paymentPlan}
          >
            Rezerwuję i przechodzę do płatności
          </ActionButton>
        </div>

        <div className="mb-50" />

        <div
          className={cn(
            'fixed bottom-0 left-0 right-0 w-full border-t border-neutral-200 bg-white p-4 shadow-2xl',
            keyboardOpen && 'hidden',
          )}
        >
          <div className="mx-auto flex max-w-[1000px] flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-2">
            <div className="text-base-text">
              Cena całkowita:{' '}
              <span className="font-medium">
                {getEstimatedPricesString(total)}
              </span>
            </div>
            <div className="flex w-full flex-row gap-2 md:w-auto">
              <ActionButton
                tone="accent"
                size="block"
                type="submit"
                isLoading={isBooking || summaryQuery.isFetching}
                disabled={!paymentPlan}
              >
                Rezerwuję
              </ActionButton>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default BookingSummaryPage;
